import { useState } from "react";
import { FileText, RotateCcw, Eye, Pencil } from "lucide-react";

const PLACEHOLDERS = [
  ["{first_name}", "Student first name"],
  ["{student_name}", "Full name"],
  ["{date}", "Date (MM/DD/YYYY)"],
  ["{school}", "School name"],
  ["{phone}", "Contact phone"],
];
const TARDY_PLACEHOLDERS = [...PLACEHOLDERS.slice(0, 3), ["{time}", "Arrival time (\" at 8:15 AM\")"], ...PLACEHOLDERS.slice(3)];

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

function renderPreview(template, phone, buildingPhone, school) {
  const d = new Date();
  const date = `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
  const vals = {
    "{first_name}": "Mousa", "{student_name}": "Mousa Ali", "{date}": date, "{time}": " at 8:15 AM", "{school}": school || "Your School",
    "{phone}": phone.trim() || buildingPhone || "the school attendance office",
  };
  return Object.entries(vals).reduce((t, [k, v]) => t.split(k).join(v), template);
}

export function LetterEditor({ id, title, hint, letter, phone, defaultLetter, buildingPhone, schoolName, onChange, disabled, showPhone, tardy }) {
  const [tab, setTab] = useState("edit");
  const value = letter || defaultLetter;
  const isDefault = !letter || letter.trim() === defaultLetter.trim();
  const tokens = tardy ? TARDY_PLACEHOLDERS : PLACEHOLDERS;

  const insert = (token) => onChange({ [id]: `${value}${value.endsWith(" ") || value.endsWith("\n") ? "" : " "}${token}` });

  return (
    <div className="border-t border-slate-100 pt-4 space-y-3" data-testid={`${id.replace(/_/g, "-")}-editor`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
          <FileText className="h-4 w-4 text-primary" /> {title}
          {isDefault && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">Default</span>}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-xs">
          <button type="button" onClick={() => setTab("edit")} data-testid={`${id}-tab-edit`}
            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 ${tab === "edit" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Pencil className="h-3 w-3" /> Edit
          </button>
          <button type="button" onClick={() => setTab("preview")} data-testid={`${id}-tab-preview`}
            className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 ${tab === "preview" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Eye className="h-3 w-3" /> Preview
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500">{hint} Blank lines start a new paragraph.</p>

      {showPhone && (
        <label className="block">
          <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Contact phone</span>
          <input value={phone} onChange={(e) => onChange({ absence_phone: e.target.value })} disabled={disabled}
            placeholder={buildingPhone || "e.g. 770-651-4900"} data-testid="letter-phone-input" className={inputCls} />
          <span className="block mt-1 text-[11px] text-slate-400">Used for {"{phone}"} in both letters. Leave blank to use the building phone{buildingPhone ? ` (${buildingPhone})` : ""}.</span>
        </label>
      )}

      {tab === "edit" ? (
        <>
          <textarea value={value} onChange={(e) => onChange({ [id]: e.target.value })} disabled={disabled} rows={10}
            data-testid={`${id}-textarea`}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          <div className="flex flex-wrap items-center gap-1.5">
            {tokens.map(([t, h]) => (
              <button key={t} type="button" title={h} onClick={() => insert(t)} disabled={disabled} data-testid={`${id}-token-${t.replace(/[{}]/g, "")}`}
                className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px] text-slate-700 hover:bg-slate-100">{t}</button>
            ))}
            {!isDefault && (
              <button type="button" onClick={() => onChange({ [id]: "" })} disabled={disabled} data-testid={`${id}-reset-btn`}
                className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                <RotateCcw className="h-3 w-3" /> Reset to default
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap" data-testid={`${id}-preview`}>
          {renderPreview(value, phone, buildingPhone, schoolName)}
        </div>
      )}
    </div>
  );
}

export function AbsenceLetterEditor(props) {
  return (
    <LetterEditor id="absence_letter" title="Absence letter" showPhone
      hint="Sent to parents by email and in the parent app whenever a student is marked absent." {...props} />
  );
}

export function TardyLetterEditor(props) {
  return (
    <LetterEditor id="tardy_letter" title="Tardy letter" tardy
      hint="Emailed to parents the moment the kiosk records a tardy arrival." {...props} />
  );
}
